import React, { FC, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import ContentHeader from "../../components/Admin/ContentHeader/ContentHeader";
import classes from "./AuditPage.module.scss";
import { commonHeaders } from "../../constants/HeadersAPI";
import { baseURL } from "../../config";

type AuditEntityType =
  | "BRANCH"
  | "QUEUE"
  | "TERMINAL"
  | "MONITOR"
  | "CATEGORY_BINDING"
  | "TOPIC_BINDING"
  | "MONITOR_TOPIC_BINDING"
  | "EMPLOYEE"
  | "WORKPLACE";

type AuditEventDto = {
  entityType: AuditEntityType;
  entityId: number | null;
  operation: "CREATE" | "UPDATE" | "DELETE" | "ACTIVATE" | "DEACTIVATE";
  description: string | null;
  details: Record<string, unknown> | null;
  createdBy: string | null;
  createdAt: string;
};

const entityTypeOptions: { value: AuditEntityType | ""; label: string }[] = [
  { value: "", label: "Все сущности" },
  { value: "BRANCH", label: "Филиалы" },
  { value: "QUEUE", label: "Очереди" },
  { value: "TERMINAL", label: "Терминалы" },
  { value: "MONITOR", label: "Табло" },
  { value: "CATEGORY_BINDING", label: "Привязки категорий" },
  { value: "TOPIC_BINDING", label: "Привязки услуг" },
  { value: "MONITOR_TOPIC_BINDING", label: "Услуги на табло" },
  { value: "EMPLOYEE", label: "Сотрудники" },
  { value: "WORKPLACE", label: "Рабочие места" },
];

const entityTypeLabels = Object.fromEntries(
  entityTypeOptions
    .filter((opt) => opt.value)
    .map((opt) => [opt.value, opt.label]),
) as Record<AuditEntityType, string>;

const operationLabels: Record<AuditEventDto["operation"], string> = {
  CREATE: "Создание",
  UPDATE: "Изменение",
  DELETE: "Удаление",
  ACTIVATE: "Активация",
  DEACTIVATE: "Деактивация",
};

function formatDateTimeLocal(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getDefaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return {
    from: formatDateTimeLocal(from),
    to: formatDateTimeLocal(to),
  };
}

function resolveRequestTo(selectedTo: string): string {
  const now = new Date();
  if (!selectedTo) {
    return formatDateTimeLocal(now);
  }

  const selected = new Date(selectedTo);
  const sameDay =
    selected.getFullYear() === now.getFullYear() &&
    selected.getMonth() === now.getMonth() &&
    selected.getDate() === now.getDate();

  if (sameDay && selected <= now) {
    return formatDateTimeLocal(now);
  }

  return selectedTo;
}

function buildAuditUrl(params: {
  entityType?: AuditEntityType | "";
  entityId?: string;
  from?: string;
  to?: string;
}): string {
  const search = new URLSearchParams();
  if (params.entityType) {
    search.set("entityType", params.entityType);
  }
  if (params.entityId) {
    search.set("entityId", params.entityId);
  }
  if (params.from) {
    search.set("from", params.from);
  }
  if (params.to) {
    search.set("to", params.to);
  }
  const qs = search.toString();
  const normalizedBase = baseURL.replace(/\/$/, "");
  return `${normalizedBase}/audit${qs ? `?${qs}` : ""}`;
}

const AuditPage: FC = () => {
  const defaultRange = React.useMemo(() => getDefaultDateRange(), []);
  const [entityType, setEntityType] = useState<AuditEntityType | "">("");
  const [entityId, setEntityId] = useState<string>("");
  const [from, setFrom] = useState<string>(defaultRange.from);
  const [to, setTo] = useState<string>(defaultRange.to);
  const [hasRequested, setHasRequested] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery<
    AuditEventDto[]
  >(
    ["auditEvents", entityType, entityId, from, to],
    () => {
      const requestTo = resolveRequestTo(to);
      return axios
        .get(
          buildAuditUrl({
            entityType,
            entityId: entityId.trim() || undefined,
            from: from || undefined,
            to: requestTo,
          }),
          commonHeaders(),
        )
        .then((r) => r.data);
    },
    {
      keepPreviousData: true,
      enabled: false,
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasRequested) {
      setHasRequested(true);
    }
    refetch();
  };

  const events = data ?? [];

  return (
    <div className={classes.container}>
      <ContentHeader name="Аудит действий" />

      <form className={classes.filters} onSubmit={handleSubmit}>
        <div className={classes.filterRow}>
          <div className={classes.filterItem}>
            <label>Тип сущности</label>
            <select
              value={entityType}
              onChange={(e) =>
                setEntityType(e.target.value as AuditEntityType | "")
              }
            >
              {entityTypeOptions.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {/* <div className={classes.filterItem}>
            <label>ID сущности (опционально)</label>
            <input
              type="number"
              min={1}
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="Например: 1"
            />
          </div> */}
        </div>

        <div className={classes.filterRow}>
          <div className={classes.filterItem}>
            <label>С даты/времени</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className={classes.filterItem}>
            <label>По дату/время</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className={classes.filterActions}>
            <button
              type="submit"
              className={classes.btnPrimary}
              disabled={isFetching}
            >
              {isFetching ? "Обновление..." : "Показать"}
            </button>
          </div>
        </div>
      </form>

      {hasRequested && (
        <div className={classes.tableWrap}>
          {isLoading && !events.length ? (
            <div className={classes.info}>Загрузка...</div>
          ) : error ? (
            <div className={classes.error}>
              Не удалось загрузить аудит. Попробуйте изменить фильтры или
              повторить позже.
            </div>
          ) : events.length === 0 ? (
            <div className={classes.info}>
              Записей аудита за выбранный период нет.
            </div>
          ) : (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Дата/время</th>
                  <th>Тип</th>
                  <th>ID</th>
                  <th>Пользователь</th>
                  <th>Операция</th>
                  <th>Описание</th>
                  <th>Детали</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, index) => (
                  <tr key={index}>
                    <td>
                      {e.createdAt
                        ? new Date(e.createdAt).toLocaleString()
                        : ""}
                    </td>
                    <td>{entityTypeLabels[e.entityType] ?? e.entityType}</td>
                    <td>{e.entityId ?? ""}</td>
                    <td>{e.createdBy ?? ""}</td>
                    <td>{operationLabels[e.operation] ?? e.operation}</td>
                    <td>{e.description ?? ""}</td>
                    <td>
                      {e.details ? (
                        <pre className={classes.details}>
                          {JSON.stringify(e.details, null, 2)}
                        </pre>
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditPage;
